import type { ComponentProps } from "react";
import { useId } from "react";
import { toast } from "sonner";

import { CreationForm } from "./creation-form";

import { useDi } from "~/entities/di";
import { ReviewQuery } from "~/entities/review";
import { NotFoundError } from "~/shared/error";
import { bounded } from "~/shared/fp";
import { Tk, useTranslation2 } from "~/shared/i18n";
import { iife } from "~/shared/no-topic";
import { useControllableState } from "~/shared/react";
import { Button } from "~/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/shared/ui/dialog";

type Props = ComponentProps<typeof Dialog> & {
  triggerProps?:
    | (Required<Pick<ComponentProps<typeof DialogTrigger>, "children">> &
        Omit<ComponentProps<typeof DialogTrigger>, "children">)
    | undefined;
};

function CreationDialog({ open, triggerProps, ...props }: Props) {
  const {
    postproc: { tc },
  } = useTranslation2();

  const [isOpen, handleOpenChange] = useControllableState({
    defaultValue: false,
    value: open,
    onChange: bounded(props, "onOpenChange"),
  });

  const { reviewService } = useDi();

  const { mutate: create, isPending: isCreating } = ReviewQuery.useCreate(reviewService);

  const formId = useId();

  const handleFormSubmit: ComponentProps<typeof CreationForm>["onSubmit"] = (data) => {
    create(data, {
      onSuccess: () => {
        toast.success(tc(Tk.REVIEW_CREATED), {
          description: tc(Tk["YOUR_REVIEW_WILL_BE_PUBLISHED_AFTER..."]),
          duration: Infinity,
        });
        handleOpenChange(false);
      },
      onError: (error) => {
        toast.error(tc(Tk.FAILED_TO_CREATE_REVIEW), {
          description: iife(() => {
            if (error instanceof NotFoundError) return tc(Tk.ORDER_NOT_FOUND);
            return tc(Tk.AN_UNEXPECTED_ERROR_OCCURRED_PLEASE_TRY_AGAIN_LATER);
          }),
        });
      },
    });
  };

  const { asChild: triggerAsChild = true, ...triggerProps_ } = triggerProps ?? {};

  return (
    <Dialog open={isOpen} {...props} onOpenChange={handleOpenChange}>
      {triggerProps_ && <DialogTrigger asChild={triggerAsChild} {...triggerProps_} />}
      <DialogContent
        className="flex max-h-4/5 flex-col md:max-w-2xl lg:max-w-4xl"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>{tc(Tk.WRITING_A_REVIEW)}</DialogTitle>
        </DialogHeader>
        <CreationForm id={formId} className="overflow-auto p-0.75" onSubmit={handleFormSubmit} />
        <DialogFooter>
          <Button disabled={isCreating} form={formId}>
            {tc(Tk.CREATE)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { CreationDialog };
